<template>
    <article>
        <p>
            We're building Coroot, an open-source observability tool that helps engineers troubleshoot their systems.
            Our "secret sauce" is that we test our product on real problems to understand the data needed, and how to analyze it
            in order to identify issues.
            In previous posts, we explored how we use Chaos Engineering to confirm that Coroot effectively detects
            <a href="/blog/chaos-driven-observability-spotting-network-failures" target="_blank">network</a> and
            <a href="/blog/spotting-postgres-performance-issues-with-coroot" target="_blank">Postgres performance</a> issues.
            Now, it's time to focus on memory-related issues.
        </p>

        <h2>Memory Management 101</h2>

        <p>
            Linux memory management is a pretty complex system, but it offers a straightforward interface for applications.
            An application can ask the kernel to allocate memory using <var>malloc()</var>, utilize the allocated memory, and then release
            memory that is no longer needed with <var>free()</var>.
        </p>

        <p>
            However, memory is a limited resource shared among the applications running on the node.
            The Linux kernel offers various methods to manage memory allocations:
        </p>

        <ul>
            <li>
                Limits: You can set the maximum amount of memory that an application is allowed to allocate.
                If the application reaches this memory limit, it will be terminated by the OOM killer.
            </li>
            <li>
                Termination priorities (<var>oom_score_adj</var>):
                An application can be terminated by the OOM (Out Of Memory) killer, even if it doesn't have a memory limit, for example, when a node runs out of memory.
                The priority for selecting which applications to terminate is based on their memory usage,
                but you can adjust the priority of any given app.
            </li>
            <li>
                Throttling (<var>memory.high</var>): Modern Linux kernel versions allows you to configure the <var>memory.high</var> threshold for an app.
                When the application reaches this threshold, its allocations are slowed down.
                The application won't be terminated, but its performance may suffer due to increased allocation latency.
            </li>
        </ul>

        <p>
            Let's take a closer look at scenarios involving OOM events and memory throttling.
            We'll explore how each of these impacts application performance and discuss methods for identifying them.
        </p>


        <h2>Out Of Memory</h2>

        <p>
            Containers in distributed systems are often considered ephemeral entities, meaning they can crash and be
            restarted by an orchestrator like Kubernetes or even be evicted from the node.
            However, when a container crashes or is terminated due to an Out Of Memory (OOM) event and then restarts,
            the application cannot finish serving in-progress requests, potentially resulting in errors in handling user requests.
        </p>

        <p>
            Another reason to monitor OOM events is that if a container restarts repeatedly, Kubernetes may introduce pauses between restart attempts
            (resulting in the <var>CrashLoopBackOff</var> state), which can lead to reduced capacity or even complete unavailability of the application.
        </p>

        <p>
            Now, let's explore how to troubleshoot such issues with Coroot.
        </p>

        <p>
            To illustrate this scenario, I've deployed a demo service called <var>catalog</var> with an intentionally introduced memory leak.
            This Go application allocates a memory buffer on each request but does not release it after the request is completed.
        </p>

        <div class="pa-5 my-6">
            <v-img class="ma-auto" src="/static/img/blog/memory-issues/catalog1.png" />
        </div>

        <p>
            Coroot offers two predefined memory inspections, which not only identify cases where application containers
            were terminated by the OOM killer but also detect memory leaks, allowing you to be proactive in addressing these issues.
        </p>

        <p>
            Okay, we've confirmed that the used memory is increasing and has hit its limit.
            What should we do next? We still need to figure out how to solve this issue.
        </p>

        <p>
            Let's dive into the profiling data to pinpoint the exact line of code responsible for the memory leak.
            When you click on the "Profile" button, Coroot will display the memory profile of the application for that specific time window.
        </p>

        <div class="pa-5 my-6">
            <v-img class="ma-auto" src="/static/img/blog/memory-issues/catalog_memory_profile1.png" />
        </div>

        <p>
            This flame graph displays the code hierarchy, organized by memory usage, with each frame representing the memory allocated by a specific function.
            A wider frame indicates greater memory usage by that function.
            Frames underneath represent nested function calls.
            To identify code with a memory leak, look for the widest frame at the bottom of the stack.
        </p>

        <p>
            In our case, the <var>(*MemoryLeaker).OnRequest</var> method consumes 88% of the allocated memory.
            We now have a clear path to resolve the issue!
        </p>


        <h2>Memory throttling</h2>

        <p>
            Now, let's see how memory allocation throttling works in practice.
            To set the <var>memory.high</var> threshold for a Kubernetes application, you will need to enable the <var><a
            href="https://kubernetes.io/blog/2023/05/05/qos-memory-resources/" target="_blank">MemoryQOS</a></var> feature flag.
        </p>

        <p>
            To achieve this on k3s, you can use the following configuration file:
        </p>

        <pre v-hl.yaml>
#/etc/rancher/k3s/config.yaml
kubelet-arg:
  - feature-gates=MemoryQoS=true
        </pre>

        <p>
            Kubernetes computes the <var>memory.high</var> value automatically using the memory request,
            limit, and <var>memoryThrottlingFactor</var>, which is set to 0.9 by default.
        </p>

        <pre v-hl.python>
memory.high = memory.request + memoryThrottlingFactor * (memory.limit - memory.request)
        </pre>

        <p>
            For <var>memory.request=100M</var> and <var>memory.limit=200M</var>: <var>memory.high=100+0.9*(200-100)=190M</var>
        </p>

        <p>
            Let's ensure that Kubernetes has configured the <var>memory.high</var> threshold for the Pod:
        </p>

        <pre v-hl.bash>
# ps aux|grep catalog
root     3679345  9.6  0.5 735952 46496 ?        Ssl  10:57   0:18 /catalog

# cat /proc/3679345/cgroup
0::/kubepods.slice/kubepods-burstable.slice/kubepods-burstable-pod78342afc_525e_4bde_ad72_299684639f68.slice/cri-containerd-c70a08490635d381f80c8dba49d608968e01c4b0c65b7cca2531aade4a24b757.scope

# cat /sys/fs/cgroup/&lt;cgroup&gt;/memory.max
209715200

# cat /sys/fs/cgroup/&lt;cgroup&gt;/memory.high
199229440
        </pre>


        <p>
            Now, let's examine how the memory consumption profile changed when it reached <var>memory.high</var>:
        </p>

        <div class="pa-5 my-6">
            <v-img class="ma-auto" max-width="600px" src="/static/img/blog/memory-issues/throttling_mem.png" />
        </div>

        <p>
            In this case, we notice that memory usage stopped increasing at some point.
            But why did this happen if our memory leak is still there?
            The reason is that when the application hit <var>memory.high</var>, its memory allocations were throttled,
            <a href="https://kubernetes.io/docs/tasks/configure-pod-container/configure-liveness-readiness-startup-probes/#define-readiness-probes" target="_blank">readiness probes</a> started to time out,
            and Kubernetes stopped sending requests to this Pod.
        </p>

        <div class="pa-5 my-6">
            <v-img class="ma-auto" src="/static/img/blog/memory-issues/throttling_instances1.png" />
        </div>

        <p>
            We still lack a complete understanding of how allocation throttling works at the low level.
            Let's explore what the application is currently doing.
        </p>

        <div class="pa-5 my-6">
            <v-img class="ma-auto" max-width="600px" src="/static/img/blog/memory-issues/throttling_cpu.png" />
        </div>

        <p>
            As shown on the chart, CPU usage has increased compared to the previous state.
            Instead of making assumptions about where the CPU time is being spent, let's examine the CPU profile in
            comparison mode to precisely identify the changes.
        </p>

        <div class="pa-5 my-6">
            <v-img class="ma-auto" src="/static/img/blog/memory-issues/cpu_profile1.png" />
        </div>

        <p>
            This mode enables a quick understanding of which function calls have started to consume more resources compared to the baseline time window.
            If we zoom in on any "red" frame, we can see that the code got stuck on memory allocations, which resulted in memory reclaim attempts.
        </p>

        <div class="pa-5 my-6">
            <v-img class="ma-auto" src="/static/img/blog/memory-issues/cpu_profile2.png" />
        </div>

        <p>
            It would be better if the OOM killer simply terminated the app.
            In this case, the application could continue to handle requests after a restart.
        </p>

        <p>
            If MemoryQOS is enabled in your cluster and you want to prevent throttling for certain apps,
            make sure that both the memory request and limit have identical values.
            This will assign the Guaranteed QoS class to the application, and Kubernetes won't set a <var>memory.high</var> threshold for it.
        </p>

        <h2>The cluster overview</h2>

        <p>
            Modern systems often consist of hundreds of services, and a single OOM event or memory leak shouldn't trigger alerts overnight.
            Nevertheless, addressing such issues is crucial for system stability.
            Coroot's Application Health Summary makes it easy to find applications in the cluster experiencing
            memory-related issues.
        </p>

        <div class="pa-5 my-6">
            <v-img class="ma-auto" src="/static/img/blog/memory-issues/health.png" />
        </div>

        <h2>Conclusion</h2>

        <p>
            In summary, your apps may encounter the following memory-related issues:
        </p>

        <ul>
            <li>
                OOM (Out of Memory): An app may be terminated by the OOM killer either due to reaching its memory limit
                or when the node runs out of memory. Coroot automatically flags this and provides a memory profile to identify problematic code.
            </li>
            <li>
                Memory Throttling: If an app hits the <var>memory.high</var> threshold, its further memory allocations can be throttled.
                In such cases, Coroot identifies issues with specific instances, and you can use its built-in CPU profiling
                to precisely understand the activity of each instance.
            </li>
        </ul>

        <p>
            Follow the instructions on our <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Getting started</router-link> page to try Coroot now.
            Not ready to Get started with Coroot? Check out our <a href="https://community-demo.coroot.com/" target="_blank">live demo</a>.
        </p>

        <p>If you like Coroot, give us a ⭐ on <a href="https://github.com/coroot/coroot" target="_blank">GitHub</a>️ or share your experience on
            <a href="https://www.g2.com/products/coroot/reviews" target="_blank">G2</a>.</p>

        <p>Any questions or feedback? Reach out to us on <a href="https://join.slack.com/t/coroot-community/shared_invite/zt-1gsnfo0wj-I~Zvtx5CAAb8vr~r~vecyw" target="_blank"><v-icon>mdi-slack</v-icon>Slack</a>.</p>
    </article>
</template>

<script>

export default {
    components: {},
}
</script>

<style scoped>
.marked {
    font-weight: 600;
    color: #F98D88;
}

.comment {
    font-weight: 600;
    color: white;
}
</style>
